import { useEffect, useReducer } from 'react';
import type { Alert } from '../types/Alert';
import { getAlerts } from '../api/alerts';
import { setTenantId } from '../api/client';
import { useAuth } from '../contexts/AuthContext';

// Module-level cache + subscriber list. Unlike the reference-data hooks (which
// are immutable for the session), the Alerts snapshot DOES change — sp_RunAlerts
// rewrites it. Both the TopBar heart-badge and the AlertsDashboardPage subscribe
// here so a single refresh propagates to both without prop-drilling. The
// SysadminTools page calls refreshAlerts() after a successful POST /api/alerts/run.
let cache: Alert[] | null = null;
// /api/alerts is tenant-scoped, so the cache is too: it records which tenant the
// data belongs to and is dropped when a consumer mounts under a different one.
let cachedForTenantId: number | null = null;
let inflight: Promise<Alert[]> | null = null;
let lastError: string | null = null;
const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach((l) => l());
}

function load(forceRefresh: boolean, tenantId: number | null): Promise<Alert[]> {
  if (!forceRefresh && cache && cachedForTenantId === tenantId) return Promise.resolve(cache);
  if (inflight) return inflight;
  // Write the tenant id into api/client.ts module state before the request
  // fires. AuthContext also writes it from its own [user] effect, but on a HARD
  // REFRESH React's child-first effect ordering runs this hook's effect first,
  // so apiGet would go out without X-Tenant-Id and the backend answers 400
  // tenant_required — observed in a browser check as a failing GET /api/alerts,
  // which leaves the TopBar badge showing no alerts when there may be some.
  // Same remedy as useFunds.
  if (tenantId !== null) setTenantId(tenantId);
  inflight = getAlerts()
    .then((data) => {
      cache = data;
      cachedForTenantId = tenantId;
      lastError = null;
      inflight = null;
      notify();
      return data;
    })
    .catch((err: unknown) => {
      inflight = null;
      lastError = err instanceof Error ? err.message : String(err);
      notify();
      throw err;
    });
  return inflight;
}

export function refreshAlerts(): Promise<Alert[]> {
  // Called after sp_RunAlerts from a screen the user is already on, so the tenant
  // is long since established; reuse whichever one the cache was built for.
  return load(true, cachedForTenantId);
}

export function clearAlertsCache(): void {
  cache = null;
  cachedForTenantId = null;
  inflight = null;
  lastError = null;
  notify();
}

interface UseAlertsResult {
  data: Alert[] | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<Alert[]>;
}

// Lazy first load on mount; subsequent components reuse the cache.
export function useAlerts(): UseAlertsResult {
  const [, forceUpdate] = useReducer((x: number) => x + 1, 0);
  const { user } = useAuth();
  const tenantId = user?.currentTenant?.tenantId ?? null;

  useEffect(() => {
    listeners.add(forceUpdate);
    // Wait for the tenant before fetching, and re-fetch if it changes: a request
    // sent without one is simply rejected, and a cache built under another tenant
    // would be the wrong list.
    if (tenantId !== null && inflight === null && (cache === null || cachedForTenantId !== tenantId)) {
      // Swallow — error surfaces via lastError above.
      load(false, tenantId).catch(() => undefined);
    }
    return () => {
      listeners.delete(forceUpdate);
    };
  }, [tenantId]);

  return {
    data: cache,
    loading: cache === null && inflight !== null,
    error: lastError,
    refresh: refreshAlerts,
  };
}
